import { isDomainError, isValidationPayload } from "@repo/api";
import { DomainError } from "@repo/domain";

export class UnconfirmedSaveError extends Error {
  constructor() {
    super("Couldn't confirm the save. Check and try again.");
  }
}

export class DomainResultError extends Error {}

export class OptimisticWriteError extends Error {
  constructor(cause: unknown) {
    super(cause instanceof Error ? cause.message : "Optimistic write failed", { cause });
  }
}

// These queued changes were never sent, even when the preceding save is uncertain.
export class CascadedWriteError extends Error {
  constructor(cause: unknown) {
    super("A preceding save failed, so this change was undone.", { cause });
  }
}

const reported = new WeakSet<object>();

export function markWriteErrorReported(error: unknown): void {
  if (typeof error === "object" && error !== null) reported.add(error);
}

/** Already surfaced through `onWriteError`, so a caller adding its own toast would show it twice. */
export function isReportedWriteError(error: unknown): boolean {
  return typeof error === "object" && error !== null && reported.has(error);
}

export function isRejectedWrite(error: unknown): boolean {
  return (
    error instanceof CascadedWriteError ||
    error instanceof OptimisticWriteError ||
    error instanceof DomainResultError ||
    isDomainError(error)
  );
}

export function writeErrorMessage(error: unknown): string {
  if (error instanceof UnconfirmedSaveError) return error.message;
  if (error instanceof DomainError) return error.message;
  if (
    (error instanceof DomainResultError || isDomainError(error)) &&
    typeof error.message === "string" &&
    !isValidationPayload(error.message)
  ) {
    return error.message;
  }
  return "Couldn't save the change. Check and try again.";
}
